/**
 * Engine-independent parsing for the date strings used across the app.
 *
 * `new Date(string)` must not be used for these values: the formats are outside the
 * ISO 8601 subset required by ECMAScript, so parsing is implementation-defined. V8
 * (desktop Chrome) accepts them, while JavaScriptCore (Safari and every browser on iOS)
 * returns `Invalid Date`.
 *
 * Two shapes are accepted, matching what the app actually produces:
 *   - `YYYY-MM-DD[ hh:mm[:ss]] [AM|PM]` as stored and rendered by AnandDate
 *   - `[Www, ]DD Mon YYYY[ hh:mm[:ss]] [AM|PM]` as displayed by the date picker, which is
 *     configured with the format `D, dd M yyyy`, for example "Sun, 21 Jan 2024"
 */

const ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[\sT]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*([AaPp])\.?[Mm]\.?)?)?$/;

const TEXT_DATE_PATTERN =
	/^(?:[A-Za-z]{3,9},?\s+)?(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})(?:[\s,]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*([AaPp])\.?[Mm]\.?)?)?$/;

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Converts a 12-hour clock hour and meridiem marker to a 24-hour hour.
 *
 * `AnandDate.format` emits `00` rather than `12` for midnight, so hour 0 is accepted for
 * AM to keep previously stored values parseable.
 */
function to24Hour(hour: number, meridiem?: string): number | null {
	if (!meridiem) {
		return hour <= 23 ? hour : null;
	}

	if (hour > 12) {
		return null;
	}

	if (meridiem === 'P') {
		return hour === 12 ? 12 : hour + 12;
	}

	return hour === 12 ? 0 : hour;
}

function buildDate(
	year: number,
	month: number,
	date: number,
	hourStr?: string,
	minuteStr?: string,
	secondStr?: string,
	meridiemStr?: string
): Date | null {
	if (month < 1 || month > 12 || date < 1 || date > 31) {
		return null;
	}

	let minutes = minuteStr ? parseInt(minuteStr, 10) : 0;
	let seconds = secondStr ? parseInt(secondStr, 10) : 0;

	if (minutes > 59 || seconds > 59) {
		return null;
	}

	let hours = to24Hour(hourStr ? parseInt(hourStr, 10) : 0, meridiemStr ? meridiemStr.toUpperCase() : undefined);

	if (hours === null) {
		return null;
	}

	let parsed = new Date(year, month - 1, date, hours, minutes, seconds, 0);

	// Rejects values that silently roll over, such as 2024-02-31.
	if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== date) {
		return null;
	}

	return parsed;
}

/**
 * Parses a date-time string into a local-time `Date`, or returns `null` when the value is
 * empty or cannot be parsed. Never returns an `Invalid Date`.
 */
export function parseDateTime(value?: string | null): Date | null {
	if (typeof value !== 'string') {
		return null;
	}

	let trimmed = value.trim();

	let isoMatch = ISO_DATE_PATTERN.exec(trimmed);
	if (isoMatch) {
		let [, year, month, date, hour, minute, second, meridiem] = isoMatch;

		return buildDate(parseInt(year, 10), parseInt(month, 10), parseInt(date, 10), hour, minute, second, meridiem);
	}

	let textMatch = TEXT_DATE_PATTERN.exec(trimmed);
	if (textMatch) {
		let [, date, monthName, year, hour, minute, second, meridiem] = textMatch;
		let month = MONTHS.indexOf(monthName.substring(0, 3).toLowerCase()) + 1;

		if (!month) {
			return null;
		}

		return buildDate(parseInt(year, 10), month, parseInt(date, 10), hour, minute, second, meridiem);
	}

	return null;
}

export default parseDateTime;
